// Public form for studios and brands to propose a video.

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import NavBar from "../../components/feature/NavBar";
import Footer from "../../components/feature/Footer";
import { db } from "../../utils/firebase";
import { useAuth } from "../../utils/AuthContext";
import { usePageMeta } from "../../hooks/usePageMeta";
import { isValidVideoInput, parseVideoSource } from "../../lib/videoSources";

const input =
  "w-full border border-neutral-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-neutral-900/20 focus:border-neutral-500";

export default function Submit() {
  usePageMeta(
    "Submit a video",
    "Made a great motion design video for a brand? Submit it to The Motion Hub for review.",
  );
  const { currentUser, loading } = useAuth();
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [brandName, setBrandName] = useState("");
  const [studioName, setStudioName] = useState("");
  const [message, setMessage] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!currentUser) return;
    if (!isValidVideoInput(url)) {
      setError("Enter the full link to the video (YouTube, Vimeo, X or a direct file).");
      return;
    }
    const email = (contactEmail || currentUser.email || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Enter an email address so we can get back to you.");
      return;
    }
    setState("sending");
    try {
      await addDoc(collection(db, "submissions"), {
        url: parseVideoSource(url).url,
        title: title.trim().slice(0, 300),
        brandName: brandName.trim().slice(0, 200),
        studioName: studioName.trim().slice(0, 200),
        message: message.trim().slice(0, 4000),
        contactEmail: email,
        uid: currentUser.uid,
        status: "new",
        createdAt: serverTimestamp(),
      });
      setState("sent");
    } catch (err) {
      console.error(err);
      setError("The submission couldn't be sent. Check your connection and try again.");
      setState("idle");
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 text-gray-800 md:px-20 px-8">
      <NavBar />
      <main className="max-w-xl mx-auto px-4 py-10 flex flex-col gap-6">
        <div className="flex flex-col gap-3">
          <h1 className="font-bold text-5xl">Submit a video</h1>
          <p className="text-neutral-600">
            Made a motion design video you're proud of? Send it to us. Every submission is reviewed by
            the team before it appears on The Motion Hub.
          </p>
        </div>

        {loading ? null : !currentUser ? (
          <div className="bg-white border border-neutral-200 rounded-lg p-6 flex flex-col gap-3">
            <p className="text-sm">Log in to submit a video, so we can reach you about it.</p>
            <div className="flex gap-2">
              <Link to="/login" className="bg-neutral-950 text-white text-sm font-medium px-4 py-2 rounded-md">
                Log in
              </Link>
              <Link to="/signup" className="border border-neutral-300 text-sm font-medium px-4 py-2 rounded-md">
                Create an account
              </Link>
            </div>
          </div>
        ) : state === "sent" ? (
          <div className="bg-white border border-emerald-200 rounded-lg p-6 flex flex-col gap-3">
            <CheckCircleIcon className="w-8 h-8 text-emerald-600" />
            <p className="font-semibold">Thanks, your video was submitted.</p>
            <p className="text-sm text-neutral-600">We'll review it and email you if it's published.</p>
            <button
              className="self-start text-sm underline"
              onClick={() => {
                setUrl("");
                setTitle("");
                setMessage("");
                setState("idle");
              }}
            >
              Submit another video
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-neutral-200 rounded-lg p-6 flex flex-col gap-4">
            <label className="flex flex-col gap-1.5 text-sm font-medium">
              Video link *
              <input className={input} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://vimeo.com/…" required />
            </label>
            <label className="flex flex-col gap-1.5 text-sm font-medium">
              Title
              <input className={input} value={title} onChange={(e) => setTitle(e.target.value)} />
            </label>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="flex flex-col gap-1.5 text-sm font-medium">
                Brand
                <input className={input} value={brandName} onChange={(e) => setBrandName(e.target.value)} />
              </label>
              <label className="flex flex-col gap-1.5 text-sm font-medium">
                Studio
                <input className={input} value={studioName} onChange={(e) => setStudioName(e.target.value)} />
              </label>
            </div>
            <label className="flex flex-col gap-1.5 text-sm font-medium">
              Anything we should know?
              <textarea
                className={input}
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Credits, a case study link, whether you can share the source file for styleframes…"
              />
            </label>
            <label className="flex flex-col gap-1.5 text-sm font-medium">
              Contact email *
              <input
                className={input}
                type="email"
                value={contactEmail}
                placeholder={currentUser.email ?? ""}
                onChange={(e) => setContactEmail(e.target.value)}
              />
            </label>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={state === "sending"}
              className="self-start bg-neutral-950 text-white text-sm font-medium px-5 py-2.5 rounded-md disabled:opacity-50"
            >
              {state === "sending" ? "Sending…" : "Submit video"}
            </button>
          </form>
        )}
      </main>
      <Footer />
    </div>
  );
}
